import { useState, type SubmitEvent, type ReactNode } from 'react';

export type TotpEnrollStep = 'password' | 'scan' | 'recovery';

export interface TotpEnrollLabels {
  passwordTitle: string;
  passwordLabel: string;
  passwordNext: string;
  scanTitle: string;
  scanInstructions: string;
  manualKey: string;
  codeLabel: string;
  codePlaceholder: string;
  confirm: string;
  recoveryTitle: string;
  recoveryIntro: string;
  download: string;
  done: string;
}

export interface TotpEnrollWizardProps {
  step: TotpEnrollStep;
  labels: TotpEnrollLabels;
  onSubmitPassword: (password: string) => void;
  onConfirmCode: (code: string) => void;
  onDone: () => void;
  qrSvg?: string;
  otpauthUri?: string;
  recoveryCodes?: string[];
  onDownload?: () => void;
  // Recovery-step "I saved these" gate. The consumer supplies the control (e.g. a
  // toggle) and the boolean that gates the Done button, keeping this portable
  // package free of any specific form widget.
  acknowledged?: boolean;
  ackControl?: ReactNode;
  isPending?: boolean;
  error?: string | null;
}

export const manualKeyFromOtpauth = (uri: string): string => {
  const m = /[?&]secret=([^&]+)/.exec(uri);
  return m ? decodeURIComponent(m[1]) : '';
};

// Presentational, controlled TOTP enrollment wizard. The consumer drives `step`
// from its async mutation results and supplies data (qrSvg, recoveryCodes) + all
// action callbacks. Clipboard/file-download are DOM concerns, so `onDownload` is
// a callback the browser app implements — this component stays Wails-portable.
export const TotpEnrollWizard = ({
  step,
  labels,
  onSubmitPassword,
  onConfirmCode,
  onDone,
  qrSvg,
  otpauthUri,
  recoveryCodes,
  onDownload,
  acknowledged,
  ackControl,
  isPending,
  error,
}: TotpEnrollWizardProps) => {
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');

  const submitPassword = (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSubmitPassword(password);
  };
  const confirmCode = (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    onConfirmCode(code.trim());
  };

  if (step === 'password') {
    return (
      <form onSubmit={submitPassword}>
        <h3 className="title is-6">{labels.passwordTitle}</h3>
        <div className="field">
          <label className="label" htmlFor="totp-pw">{labels.passwordLabel}</label>
          <div className="control">
            <input id="totp-pw" className="input" type="password" autoFocus value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
        </div>
        {error && <p className="help is-danger">{error}</p>}
        <div className="field">
          <div className="control">
            <button type="submit" className="button is-success" disabled={isPending}>{labels.passwordNext}</button>
          </div>
        </div>
      </form>
    );
  }

  if (step === 'scan') {
    return (
      <form onSubmit={confirmCode}>
        <h3 className="title is-6">{labels.scanTitle}</h3>
        <p className="mb-3">{labels.scanInstructions}</p>
        {qrSvg && <img src={qrSvg} alt={labels.scanTitle} style={{ width: 200, height: 200 }} />}
        {otpauthUri && (
          <p className="is-size-7 mt-2">
            {labels.manualKey}: <code>{manualKeyFromOtpauth(otpauthUri)}</code>
          </p>
        )}
        <div className="field mt-4">
          <label className="label" htmlFor="totp-code">{labels.codeLabel}</label>
          <div className="control">
            <input id="totp-code" className="input" type="text" inputMode="numeric" autoComplete="one-time-code" placeholder={labels.codePlaceholder} value={code} onChange={(e) => setCode(e.target.value)} />
          </div>
        </div>
        {error && <p className="help is-danger">{error}</p>}
        <div className="field">
          <div className="control">
            <button type="submit" className="button is-success" disabled={isPending}>{labels.confirm}</button>
          </div>
        </div>
      </form>
    );
  }

  // step === 'recovery'
  return (
    <div>
      <h3 className="title is-6">{labels.recoveryTitle}</h3>
      <p className="mb-3">{labels.recoveryIntro}</p>
      <ul className="is-family-monospace mb-4">
        {(recoveryCodes ?? []).map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>
      {onDownload && (
        <button type="button" className="button is-small mb-4" onClick={onDownload}>
          {labels.download}
        </button>
      )}
      {ackControl && <div className="mb-3">{ackControl}</div>}
      <button type="button" className="button is-success" disabled={!acknowledged} onClick={onDone}>
        {labels.done}
      </button>
    </div>
  );
};
